import { IProductsIndexState } from './index'
import { IFacetSelections } from '../../types/facets'
import { sortOptionFromString } from '../../components/sort-options'

enum QueryParamKey {
  CATEGORY = 'category',
  PAGE = 'page',
  ITEMS_PER_PAGE = 'items-per-page',
  SORT = 'sort',
  ON_SALE = 'on-sale',
  PRODUCT_TYPE = 'type',
  KEYWORD_SEARCH_QUERY = 'q',
}

const KNOWN_FACET_IDS: string[] = [
  'age-group',
  'airprint',
  'audio-in-left-and-right',
  'audio-out-left-and-right',
  'aux-input',
  'batteries-included',
  'bluetooth-enabled',
  'book-format',
  'brand',
  'built-in-microphone',
  'cables-included',
  'camera-model-compatibility',
  'card-reader',
  'clothing-style',
  'colour',
  'compatible-platforms',
  'connected-home-features',
  'consumer-advice',
  'digital-audio-inputs',
  'digital-zoom',
  'dslr',
  'exterior',
  'fabric',
  'face-detection',
  'format',
  'gender',
  'gps-enabled',
  'headphone-connection-output',
  'headphone-out',
  'image-stabilisation',
  'infant-age-range',
  'intelligent-auto',
  'lens-mount',
  'material',
  'metal-colour',
  'microphone',
  'mirrorless',
  'monitor-included',
  'motion-detection',
  'night-vision',
  'noise-cancelling',
  'noise-reduction',
  'number-of-seats',
  'occasion',
  'optical-drive',
  'optional-cloud-recording-services',
  'pattern',
  'powered',
  'price',
  'primed',
  'printer-copy-function',
  'rca-outputs',
  'remote-monitoring',
  'safety-alerts',
  'scan',
  'shade-group-name',
  'shoe-material',
  'shoe-style',
  'shop-by-fit',
  'size',
  'smile-detection',
  'sofa-size',
  'sofa-style',
  'solar',
  'spoken-street-names',
  'sport',
  'store',
  'time-lapse',
  'touchscreen',
  'usb-charging',
  'video-calling',
  'virtual-assistant-built-in',
  'voice-control',
  'wall-mountable',
  'weatherproof',
  'webcam',
  'wi-fi',
  'wireless',
  'wireless-lan',
  'with-bag',
  'works-with-amazon-alexa',
  'works-with-google-assistant',
]

const generateQueryObject = (
  state: { query?: string } & Omit<IProductsIndexState, 'query'>
): URLSearchParams => {
  const usp = new URLSearchParams()
  for (const facet of state.facetSelections) {
    for (const option of facet.selections) {
      usp.append(facet.id, option)
    }
  }
  for (const type of state.productTypes) {
    usp.append(QueryParamKey.PRODUCT_TYPE, type)
  }
  if (state.onSale) {
    usp.append(QueryParamKey.ON_SALE, state.onSale.toString())
  }
  if (state.sort) {
    usp.append(QueryParamKey.SORT, state.sort)
  }
  if (state.itemsPerPage) {
    usp.append(QueryParamKey.ITEMS_PER_PAGE, state.itemsPerPage.toString())
  }
  if (state.page !== undefined) {
    usp.append(QueryParamKey.PAGE, (state.page + 1).toString())
  }
  if (state.query) {
    usp.append(QueryParamKey.KEYWORD_SEARCH_QUERY, state.query)
  }
  return usp
}

export const createUrlSearchString = (
  state: { query?: string } & Omit<IProductsIndexState, 'query'>
): string => {
  return generateQueryObject(state).toString()
}

const makeFacetSelectionsFromQueryParamValues = (
  queryParams: URLSearchParams
): IFacetSelections => {
  const facetSelections = new Map<string, string[]>()
  queryParams.forEach((value, key) => {
    if (!KNOWN_FACET_IDS.includes(key)) {
      return
    }
    let s = facetSelections.get(key)
    if (!s) {
      s = []
      facetSelections.set(key, s)
    }
    s.push(value)
  })
  return Array.from(facetSelections.entries(), ([id, selections]) => ({
    id,
    selections,
  }))
}

export const makeProductsIndexState = (
  usp: URLSearchParams,
  productsIndexStateDefaults: IProductsIndexState
): IProductsIndexState => {
  const state = { ...productsIndexStateDefaults }

  const sort = sortOptionFromString(usp.get(QueryParamKey.SORT) ?? '')
  if (sort) {
    state.sort = sort
  } else {
    state.sort = productsIndexStateDefaults.sort
  }

  const page = parseInt(usp.get(QueryParamKey.PAGE) ?? '', 10)
  if (!isNaN(page)) {
    state.page = Math.max(0, page - 1)
  } else {
    state.page = productsIndexStateDefaults.page
  }

  const itemsPerPage = parseInt(usp.get(QueryParamKey.ITEMS_PER_PAGE) ?? '', 10)
  if (!isNaN(itemsPerPage)) {
    state.itemsPerPage = itemsPerPage
  } else {
    state.itemsPerPage = productsIndexStateDefaults.itemsPerPage
  }

  state.onSale = onSaleQueryParamValue(usp)
  state.facetSelections = makeFacetSelectionsFromQueryParamValues(usp)

  state.productTypes = usp.getAll(QueryParamKey.PRODUCT_TYPE)

  state.query = keywordSearchQueryParamValue(usp)

  return state
}

// Note: allow list approach due to leaf categories being encoded as query params
const KNOWN_TRANSFERABLE_QUERY_PARAMS: string[] = [
  QueryParamKey.ITEMS_PER_PAGE,
  QueryParamKey.ON_SALE,
  QueryParamKey.SORT,
  QueryParamKey.KEYWORD_SEARCH_QUERY,
]

export const getTransferableUrlSearchParams = (
  facetIds: string[],
  urlSearchParams: URLSearchParams
): string => {
  const usp = new URLSearchParams(urlSearchParams)
  urlSearchParams.forEach((_, key) => {
    if (
      !(KNOWN_TRANSFERABLE_QUERY_PARAMS.includes(key) || facetIds.includes(key))
    ) {
      usp.delete(key)
    }
  })
  return usp.toString()
}

export function onSaleQueryParamValue(usp: URLSearchParams): boolean {
  return usp.get(QueryParamKey.ON_SALE) === 'true'
}

export function keywordSearchQueryParamValue(usp: URLSearchParams): string {
  return usp.get(QueryParamKey.KEYWORD_SEARCH_QUERY) || ''
}
